import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Info, CarFront, Cog, Wrench, AlertTriangle, Image as ImageIcon, FileText, FileSearch, ArrowLeft, CalendarClock, Camera, Gauge, Fuel, Activity, FileDown, Pencil, ArrowLeftRight, TrendingUp, ShieldCheck, BadgeCheck, ClipboardList, Copy, Umbrella, CalendarDays, CheckCircle2, Clock3, ChevronDown, StickyNote } from "lucide-react";
import { api, photoUrl, formatApiError } from "../api";
import { scoreColor, fmtKm, fmtDate, fmtTry, EVENT_META, belgeTarih, belgeDurumu, fmtDateShort } from "../lib/labels";
import { autoVehicleImage, hasOwnPhoto } from "../lib/vehicleImages";
import InfoSection from "../components/detail/InfoSection";
import PaintSection from "../components/detail/PaintSection";
import NotesSection from "../components/detail/NotesSection";
import BelgelerSection from "../components/detail/BelgelerSection";
import MaintenanceSection from "../components/detail/MaintenanceSection";
import RecordsSection from "../components/detail/RecordsSection";
import PhotosSection from "../components/detail/PhotosSection";
import ReportSection from "../components/detail/ReportSection";

const TABS = [
  { key: "bilgiler", label: "Araç Bilgileri", Icon: Info },
  { key: "kaporta", label: "Kaporta / Boya", Icon: CarFront },
  { key: "bakim", label: "Bakım Geçmişi", Icon: Wrench },
  { key: "hasar", label: "Hasar / Kaza", Icon: AlertTriangle },
  { key: "notlar", label: "Notlar", Icon: StickyNote },
  { key: "fotograflar", label: "Araç Fotoğrafları", Icon: ImageIcon },
  { key: "belgeler", label: "Belgeler", Icon: CalendarClock },
  { key: "rapor", label: "Rapor", Icon: FileText },
];

const BELGE_CARDS = [
  { key: "kasko", label: "Kasko", Icon: Umbrella },
  { key: "sigorta", label: "Trafik Sigortası", Icon: FileText },
  { key: "muayene", label: "Muayene", Icon: CalendarDays },
];

const glassCard = "bg-[#07223A]/70 backdrop-blur-xl border border-cyan-500/35 rounded-2xl shadow-[0_0_22px_rgba(249,115,22,0.16)]";

const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
};

const statusGlow = (iso) => {
  const d = daysLeft(iso);
  if (d === null) return "border-white/10";
  if (d < 0) return "border-red-500/50 shadow-[0_0_18px_rgba(255,59,48,0.2)]";
  if (d <= 30) return "border-amber-500/50 shadow-[0_0_18px_rgba(255,204,0,0.18)]";
  return "border-emerald-500/40 shadow-[0_0_18px_rgba(52,199,89,0.18)]";
};

const pillCls = (iso) => {
  const d = daysLeft(iso);
  if (d === null) return "border-white/15 bg-slate-800/60 text-slate-300";
  if (d < 0) return "border-red-400/60 bg-red-500/15 text-red-300 shadow-[0_0_18px_rgba(255,59,48,0.25)]";
  if (d <= 30) return "border-amber-400/60 bg-amber-500/15 text-amber-300 shadow-[0_0_18px_rgba(255,204,0,0.2)]";
  return "border-sky-400/60 bg-sky-500/15 text-sky-300 shadow-[0_0_18px_rgba(0,180,255,0.25)]";
};

const actionBtn = "flex items-center gap-2.5 px-5 py-3 rounded-xl border text-sm font-bold transition-all w-full justify-start";
const actionOutline = `${actionBtn} bg-[#061A2E]/70 border-cyan-500/40 text-cyan-200 hover:bg-cyan-950/50 hover:border-cyan-300`;

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("bilgiler");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const photoRef = useRef(null);
  const tabsRef = useRef(null);

  useEffect(() => {
    setVehicle(null);
    setError("");
    api.get(`/my-vehicles/${id}`)
      .then((r) => setVehicle(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [id]);

  const changePhoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f || !vehicle) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { toast.error("Sadece JPG veya PNG yükleyebilirsiniz"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Fotoğraf 5 MB'ı aşamaz"); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      const res = await api.put(`/my-vehicles/${vehicle.id}/photo`, { photo: data.path });
      setVehicle(res.data);
      toast.success("Araç fotoğrafı güncellendi");
    } catch (err) { toast.error(formatApiError(err)); }
    setPhotoBusy(false);
  };

  const goTab = (t) => {
    setTab(t);
    setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const copyVin = async () => {
    try {
      await navigator.clipboard.writeText(vehicle.vin);
      toast.success("Şasi No kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen pt-28 max-w-2xl mx-auto px-4 text-center">
        <p data-testid="detail-error" className="text-red-400">{error}</p>
        <Link to="/araclarim" className="inline-flex items-center gap-2 mt-5 text-cyan-300 hover:text-cyan-200 text-sm">
          <ArrowLeft className="w-4 h-4" /> Araçlarıma dön
        </Link>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const own = hasOwnPhoto(vehicle);
  const img = own ? photoUrl(vehicle.photo || vehicle.photos[0]) : autoVehicleImage(vehicle.brand, vehicle.model);
  const hist = vehicle.history || [];
  const byTypes = (types) => hist.filter((h) => types.includes(h.type)).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const sonServis = byTypes(["servis"])[0];
  const sonEkspertiz = byTypes(["ekspertiz"])[0];
  const tramer = byTypes(["kaza", "hasar"]);
  const tramerCost = tramer.reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const kmVals = hist.filter((h) => h.km != null).map((h) => Number(h.km));
  const sonKm = kmVals.length ? Math.max(...kmVals) : null;
  const belgeler = belgeTarih(vehicle.belgeler);
  const lastRecords = [...hist].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 4);
  const updatedAt = vehicle.updated_at || vehicle.created_at;
  const updatedText = updatedAt ? new Date(updatedAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;

  const heroSpecs = [
    { Icon: Gauge, label: "Kilometre", value: fmtKm(vehicle.km), testid: "km" },
    { Icon: Fuel, label: "Yakıt", value: vehicle.fuel || "—", testid: "fuel" },
    { Icon: Cog, label: "Vites", value: vehicle.transmission || "—", testid: "transmission" },
    { Icon: CarFront, label: "Kasa Tipi", value: vehicle.body_type || "—", testid: "body-type" },
    { Icon: CarFront, label: "Araç Türü", value: vehicle.vehicle_type || "Otomobil", testid: "vehicle-type" },
    { Icon: Activity, label: "Son Doğrulanan KM", value: sonKm != null ? fmtKm(sonKm) : "—", testid: "verified-km" },
  ];

  const midCards = [
    {
      Icon: TrendingUp, label: "Güncel Piyasa Değeri", testid: "piyasa",
      value: "Canlı veri bekleniyor", valueCls: "text-amber-300",
      action: "Değerleme sayfasına git →", onClick: () => navigate("/degerleme"),
    },
    {
      Icon: FileText, label: "Tramer Özeti", testid: "tramer",
      value: tramer.length === 0 ? "Hasar kaydı yok" : `${tramer.length} kayıt${tramerCost ? ` - ${fmtTry(tramerCost)}` : ""}`,
      valueCls: tramer.length === 0 ? "text-emerald-400" : "text-slate-100",
      action: "Detayları Gör →", onClick: () => goTab("hasar"),
    },
    {
      Icon: Wrench, label: "Son Ekspertiz", testid: "ekspertiz",
      value: sonEkspertiz ? fmtDate(sonEkspertiz.date) : "Kayıt yok",
      valueCls: sonEkspertiz ? "text-slate-100" : "text-slate-500",
      action: "Ekspertiz Ekle →", onClick: () => goTab("hasar"),
    },
    {
      Icon: Cog, label: "Son Servis", testid: "servis",
      value: sonServis ? fmtDate(sonServis.date) : "Kayıt yok",
      valueCls: sonServis ? "text-slate-100" : "text-slate-500",
      action: "Servis Kaydı Ekle →", onClick: () => goTab("bakim"),
    },
  ];

  return (
    <div data-testid="vehicle-detail-page" className="min-h-screen pb-0">
      {/* ÜST BANT — neon şehir + ışıklı yol */}
      <div className="relative overflow-hidden pt-24 sm:pt-28 pb-8">
        <div className="absolute inset-0">
          <img src="/detail-band.jpg" alt="" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#030C16]/40 via-transparent to-[#F6FAFD]" />
        </div>
        <div className="relative max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 flex items-end justify-between gap-4">
          <span className="font-script text-2xl sm:text-4xl on-img-cyan drop-shadow-[0_0_14px_rgba(249,115,22,0.5)] leading-tight">
            Geçmişi Bil<br />Güvenle Yola Çık
          </span>
          <span className="font-script text-2xl sm:text-4xl on-img-cyan drop-shadow-[0_0_14px_rgba(249,115,22,0.5)] leading-tight text-right">
            Güvenli<br />Araç Geleceği
          </span>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Link data-testid="detail-back-link" to="/araclarim"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-cyan-300 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Araçlarım
        </Link>

        {/* ANA KART — foto | başlık+spec | aksiyonlar */}
        <div className={`mt-3 ${glassCard} p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-[330px_minmax(0,1fr)_230px] gap-6`}>
          <div>
            <input ref={photoRef} type="file" accept="image/jpeg,image/png" className="hidden"
              data-testid="detail-photo-input" onChange={changePhoto} />
            <div className="relative w-full h-56 lg:h-full lg:min-h-[300px] rounded-xl overflow-hidden border border-cyan-500/30 bg-slate-950/60 shadow-[0_0_25px_rgba(249,115,22,0.15)]">
              <img src={img} alt={`${vehicle.brand} ${vehicle.model}`} className="w-full h-full object-cover" />
              {!own && (
                <span data-testid="detail-stock-badge"
                  className="absolute top-2.5 left-2.5 text-[11px] font-bold px-2.5 py-1 rounded-md bg-slate-950/85 border border-white/10 text-slate-300 flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3" /> Örnek Görsel
                </span>
              )}
              <button data-testid="detail-photo-change-btn" onClick={() => photoRef.current?.click()} disabled={photoBusy}
                className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-950/80 backdrop-blur border border-cyan-500/50 text-cyan-200 text-sm font-bold hover:bg-cyan-950/60 hover:border-cyan-300 transition-all disabled:opacity-50">
                <Camera className="w-4 h-4" /> {photoBusy ? "Yükleniyor..." : own ? "Fotoğrafı Değiştir" : "Fotoğraf Ekle"}
              </button>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
                {vehicle.brand} {vehicle.model} <span className="text-slate-300">{vehicle.year}</span>
              </h1>
              {vehicle.trim && (
                <span data-testid="detail-trim" className="text-xs font-bold px-3.5 py-1.5 rounded-full border border-cyan-400/60 text-cyan-200 shadow-[0_0_12px_rgba(249,115,22,0.25)]">{vehicle.trim}</span>
              )}
              <span data-testid="detail-score" className="text-sm font-bold px-3.5 py-1.5 rounded-full border"
                style={{ color: scoreColor(vehicle.score), borderColor: `${scoreColor(vehicle.score)}66`, backgroundColor: `${scoreColor(vehicle.score)}12` }}>
                Skor: {vehicle.score}
              </span>
            </div>
            <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Şasi No (VIN)</p>
            <p className="mt-1 flex items-center gap-2 font-mono-vin text-sm sm:text-base text-cyan-300 tracking-widest drop-shadow-[0_0_10px_rgba(249,115,22,0.4)]">
              {vehicle.vin}{vehicle.plate ? ` – ${vehicle.plate}` : ""}
              <button data-testid="detail-vin-copy" onClick={copyVin} className="text-slate-500 hover:text-cyan-300 transition-colors" title="Kopyala">
                <Copy className="w-4 h-4" />
              </button>
            </p>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {heroSpecs.map((s) => (
                <div key={s.testid} data-testid={`detail-spec-${s.testid}`}
                  className="rounded-xl border border-cyan-500/30 bg-[#0A2A45]/60 px-4 py-3.5 hover:border-cyan-400/50 hover:shadow-[0_0_15px_rgba(249,115,22,0.15)] transition-all">
                  <s.Icon className="w-6 h-6 text-cyan-300 drop-shadow-[0_0_8px_rgba(249,115,22,0.5)]" />
                  <p className="mt-2 text-xs font-medium text-slate-400">{s.label}</p>
                  <p className="text-base sm:text-lg font-extrabold text-white">{s.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            <button data-testid="detail-report-btn" onClick={() => goTab("rapor")}
              className={`${actionBtn} bg-gradient-to-r from-cyan-400 to-cyan-500 border-transparent text-slate-950 shadow-[0_0_20px_rgba(249,115,22,0.4)] hover:shadow-[0_0_30px_rgba(249,115,22,0.65)]`}>
              <FileSearch className="w-5 h-5" /> Detaylı Rapor
            </button>
            <button data-testid="detail-pdf-button" onClick={() => window.open(`/rapor/${vehicle.vin}`, "_blank")} className={actionOutline}>
              <FileDown className="w-5 h-5" /> PDF Rapor
            </button>
            <button data-testid="detail-edit-btn" onClick={() => goTab("bilgiler")} className={actionOutline}>
              <Pencil className="w-5 h-5" /> Düzenle
            </button>
            <button data-testid="detail-transfer-btn" onClick={() => navigate("/araclarim")} className={actionOutline}>
              <ArrowLeftRight className="w-5 h-5" /> Devret / Paylaş
            </button>
            {updatedText && (
              <div className="mt-auto pt-3 text-[11px] text-slate-500">
                <p>Son güncelleme: {updatedText}</p>
                <p data-testid="detail-fresh-badge" className="mt-1 flex items-center gap-1 text-emerald-400 font-semibold">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Sistemde güncel
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ORTA KARTLAR */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {midCards.map((c) => (
            <div key={c.testid} data-testid={`detail-card-${c.testid}`} className={`${glassCard} p-5 flex items-start gap-4`}>
              <span className="w-12 h-12 rounded-xl bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(249,115,22,0.2)]">
                <c.Icon className="w-6 h-6 text-cyan-300" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-slate-200">{c.label}</span>
                <span className={`block mt-1 text-sm sm:text-base font-extrabold ${c.valueCls}`}>{c.value}</span>
                <button data-testid={`detail-card-${c.testid}-action`} onClick={c.onClick}
                  className="mt-1.5 text-xs font-bold text-cyan-300 hover:text-cyan-200 transition-colors">
                  {c.action}
                </button>
              </span>
            </div>
          ))}
        </div>

        {/* KİLOMETRE TUTARLILIK KONTROLÜ */}
        {vehicle.km_check && vehicle.km_check.kontrol_edildi && (
          <div data-testid="detail-km-check"
            className={`mt-4 rounded-2xl border p-5 ${vehicle.km_check.durum === "tespit_edildi" ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`}>
            <p className={`text-sm sm:text-base font-extrabold ${vehicle.km_check.durum === "tespit_edildi" ? "text-red-300" : "text-emerald-300"}`}>
              {vehicle.km_check.durum === "tespit_edildi" ? "🔴" : "🟢"} {vehicle.km_check.ozet}
            </p>
            {vehicle.km_check.bulgular.map((b, i) => (
              <p key={i} data-testid={`detail-km-finding-${i}`} className="mt-2 text-xs sm:text-sm text-slate-300 leading-relaxed">{b.mesaj}</p>
            ))}
            <p className="mt-2 text-[11px] text-slate-500">
              {vehicle.km_check.karsilastirilan_kayit} kilometre kaydı tarih sırasına göre kronolojik olarak karşılaştırıldı.
              {vehicle.km_check.durum !== "tespit_edildi" && " Tüm kayıtlar arasında kilometre düşüşü bulunamadı."}
            </p>
          </div>
        )}

        {/* DURUM PILL'LERİ */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
          {BELGE_CARDS.map((b) => {
            const t = belgeler[b.key];
            const st = belgeDurumu(t.bitis);
            return (
              <span key={b.key} data-testid={`detail-pill-${b.key}`}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full border text-sm font-bold ${pillCls(t.bitis)}`}>
                <b.Icon className="w-4 h-4" />
                {b.label}: {t.bitis ? fmtDateShort(t.bitis) : st.label}
              </span>
            );
          })}
        </div>

        {/* BELGE KARTLARI */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {BELGE_CARDS.map((b) => {
            const t = belgeler[b.key];
            const st = belgeDurumu(t.bitis);
            return (
              <div key={b.key} data-testid={`detail-belge-${b.key}`}
                className={`rounded-2xl border bg-[#04121f]/80 backdrop-blur-xl p-5 transition-all ${statusGlow(t.bitis)}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2.5 text-base font-bold text-white">
                    <b.Icon className="w-6 h-6 text-cyan-300" /> {b.label}
                  </span>
                  <span data-testid={`detail-belge-status-${b.key}`} className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                </div>
                <p className="mt-3 text-sm text-slate-400">Başlangıç: <span className="text-slate-200 font-semibold">{fmtDateShort(t.baslangic)}</span> | Bitiş: <span className="text-slate-200 font-semibold">{fmtDateShort(t.bitis)}</span></p>
              </div>
            );
          })}
        </div>

        {/* GEÇMİŞ KAYITLARI */}
        <div className={`mt-4 ${glassCard} p-5`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button data-testid="detail-history-count" onClick={() => setHistOpen(!histOpen)}
              className="flex items-center gap-2.5 text-base font-bold text-white hover:text-cyan-200 transition-colors">
              <span className="w-9 h-9 rounded-xl bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center">
                <Clock3 className="w-5 h-5 text-cyan-300" />
              </span>
              Geçmiş Kayıtları ({hist.length})
              <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${histOpen ? "rotate-180" : ""}`} />
            </button>
            <button data-testid="detail-history-all" onClick={() => goTab("bakim")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-cyan-500/40 text-cyan-300 text-sm font-bold hover:bg-cyan-950/40 hover:border-cyan-300 transition-all">
              Tüm Geçmiş Kayıtlarını Gör →
            </button>
          </div>
          {histOpen && (
            <div className="mt-4 space-y-2.5">
              {lastRecords.length === 0 ? (
                <p className="text-sm text-slate-500">Henüz kayıt bulunmuyor.</p>
              ) : lastRecords.map((r) => {
                const meta = EVENT_META[r.type] || EVENT_META.tescil;
                return (
                  <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/5 bg-slate-950/50 px-4 py-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${meta.ring} ${meta.color}`}>{meta.label}</span>
                      <span className="text-sm font-semibold text-slate-100 truncate">{r.title}</span>
                    </div>
                    <span className="text-xs text-slate-400">{fmtDate(r.date)}{r.km != null && ` • ${fmtKm(r.km)}`}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* SEKMELER */}
        <div ref={tabsRef} className="mt-8 -mx-4 px-4 overflow-x-auto scroll-mt-24">
          <div data-testid="detail-tabs" className="flex gap-2 min-w-max pb-2 lg:flex-wrap lg:min-w-0">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`detail-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all whitespace-nowrap ${
                  tab === t.key
                    ? "bg-cyan-400/15 border-cyan-400/60 text-cyan-200 shadow-[0_0_16px_rgba(249,115,22,0.3)]"
                    : "border-white/10 text-slate-400 hover:text-slate-200 hover:border-cyan-500/30"
                }`}>
                <t.Icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {tab === "bilgiler" && <InfoSection vehicle={vehicle} onSaved={setVehicle} />}
          {tab === "kaporta" && <PaintSection vehicle={vehicle} onSaved={setVehicle} />}
          {tab === "notlar" && <NotesSection vehicle={vehicle} onSaved={setVehicle} />}
          {tab === "bakim" && <MaintenanceSection vehicle={vehicle} onSaved={setVehicle} />}
          {tab === "hasar" && (
            <RecordsSection vehicle={vehicle} kind="hasar" title="Hasar / Kaza Geçmişi"
              desc="Hasar ve kaza kayıtlarını bölge, tür ve fotoğraflarla birlikte ekleyin."
              Icon={AlertTriangle} testid="section-hasar" onSaved={setVehicle} />
          )}
          {tab === "fotograflar" && <PhotosSection vehicle={vehicle} onSaved={setVehicle} />}
          {tab === "belgeler" && <BelgelerSection vehicle={vehicle} onSaved={setVehicle} />}
          {tab === "rapor" && <ReportSection vehicle={vehicle} />}
        </div>
      </div>

      {/* ALT MARKA */}
      <div className="mt-12 border-t border-cyan-500/20 bg-[#030C16] py-6">
        <p data-testid="detail-brand-message" className="text-center text-sm sm:text-base text-slate-300 px-4 flex items-center justify-center gap-2 flex-wrap">
          <ShieldCheck className="w-5 h-5 text-cyan-300" />
          <span className="font-heading font-extrabold text-white">OTO<span className="text-cyan-300">KÜNYE</span></span>
          <span className="text-slate-600">|</span>
          Her aracın bir geçmişi vardır, biz o geçmişi sizin için aydınlatırız.
        </p>
      </div>
    </div>
  );
}
